import { useCallback, useContext, useEffect, useState } from "react";
import axiosAdmin from "../../../../api/apiAdmin";
import { successToast, errorToast } from "../../toastAlerts/ToastAlerts";
import { apiError } from "../../../../api/apiError";
import { AdminPrimaryContext } from "../../adminContext/AdminContext";
import { BsEnvelope, BsPhone, BsPeople, BsExclamationTriangle } from "react-icons/bs";

/**
 * Mass email / SMS to an audience picked by parcel state.
 *
 * The audience is always previewed before it can be sent: the count is fetched
 * on every filter change and passed back with the send as `expected_count`, so
 * a filter that changed underneath the operator is rejected rather than
 * silently reaching a different set of people.
 *
 * Unlike the direct panel above, this does NOT deliver inline — recipients are
 * queued and the cron drains them between 09:00 and 23:00.
 */
const STATUSES = [
  { value: "", label: "ნებისმიერი სტატუსი" },
  { value: "0", label: "0 — შექმნილი" },
  { value: "1", label: "1 — საწყობშია" },
  { value: "2", label: "2 — გამოგზავნილი" },
  { value: "3", label: "3 — ჩამოსული" },
  { value: "4", label: "4 — გაცემული" },
];

const BulkSendPanel = () => {
  const { foreignAdresses } = useContext(AdminPrimaryContext);

  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");

  const [filters, setFilters] = useState({
    all: false,
    pack_status: "",
    abroad_office_id: "",
    shipment_code: "",
    unpaid: false,
    undeclared: false,
    no_address: false,
    user_id: "",
  });

  const [audience, setAudience] = useState(null);
  const [counting, setCounting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSample, setShowSample] = useState(false);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  // Only the keys the API understands, and only when they carry a value —
  // an empty string would otherwise read as "filter on empty".
  const apiParams = useCallback(() => {
    const p = {};
    if (filters.user_id.trim()) {
      p.user_id = filters.user_id
        .split(/[\s,]+/)
        .map((v) => v.trim())
        .filter(Boolean);
      return p;
    }
    if (filters.all) {
      p.all = true;
      return p;
    }
    if (filters.pack_status !== "") p.pack_status = filters.pack_status;
    if (filters.abroad_office_id !== "") p.abroad_office_id = [filters.abroad_office_id];
    if (filters.shipment_code.trim()) p.shipment_code = [filters.shipment_code.trim()];
    if (filters.unpaid) p.unpaid = true;
    if (filters.undeclared) p.undeclared = true;
    if (filters.no_address) p.no_address = true;
    return p;
  }, [filters]);

  const loadAudience = useCallback(async () => {
    setCounting(true);
    try {
      const { data } = await axiosAdmin.get("/messages/audience", {
        params: apiParams(),
      });
      setAudience(data);
    } catch (err) {
      setAudience(null);
      errorToast(apiError(err, "მიმღებების დათვლა ვერ მოხერხდა"));
    }
    setCounting(false);
  }, [apiParams]);

  useEffect(() => {
    loadAudience();
  }, [loadAudience]);

  // How many will actually receive it on the chosen channel — an account with
  // no phone can't be SMSed, so the headline number would overstate the send.
  const reachable =
    audience == null
      ? 0
      : channel === "email"
      ? audience.with_email
      : channel === "sms"
      ? audience.with_phone
      : Math.max(audience.with_email, audience.with_phone);

  const send = async (force = false) => {
    if (!text.trim()) {
      errorToast("შეავსეთ ტექსტი");
      return;
    }
    if (!audience?.total) {
      errorToast("ამ ფილტრით მიმღები არ არის");
      return;
    }
    if (
      !window.confirm(
        `შეტყობინება გაეგზავნება ${reachable} მომხმარებელს (${
          channel === "email" ? "მეილი" : channel === "sms" ? "SMS" : "მეილი + SMS"
        }). გავაგრძელოთ?`
      )
    )
      return;

    setSending(true);
    try {
      const { data } = await axiosAdmin.post("/messages/bulk", {
        ...apiParams(),
        channel,
        subject,
        text,
        expected_count: audience.total,
        ...(force ? { force: true } : {}),
      });
      successToast(`რიგში ჩაემატა ${data.queued} შეტყობინება`);
      setText("");
    } catch (err) {
      const d = err?.response?.data;
      // Over the batch limit the API asks for an explicit confirmation rather
      // than refusing outright — offer it instead of a dead end.
      if (d?.needs_force && window.confirm(`${d.error}\n\nგავაგრძელოთ?`)) {
        setSending(false);
        return send(true);
      }
      errorToast(apiError(err, "გაგზავნა ვერ მოხერხდა"));
    }
    setSending(false);
  };

  const Check = ({ k, label }) => (
    <label className="flex items-center gap-2 text-xs dark:text-slate-200 cursor-pointer">
      <input
        type="checkbox"
        checked={filters[k]}
        onChange={(e) => set(k, e.target.checked)}
      />
      {label}
    </label>
  );

  const inputCls =
    "h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md text-sm " +
    "bg-white dark:bg-slate-800 text-slate-800 dark:text-white";

  return (
    <div className="bg-white dark:bg-slate-700 rounded-lg p-5">
      <h3 className="font-bold dark:text-white mb-1 flex items-center gap-2">
        <BsPeople /> მასობრივი გაგზავნა
      </h3>
      <p className="text-xs text-slate-500 dark:text-slate-300 mb-4">
        მიმღებები აირჩევა ამანათის მდგომარეობით. გაგზავნა ხდება რიგში — რეალურად
        იგზავნება 09:00–23:00 შუალედში, ყოველ 5 წუთში.
      </p>

      {/* Channel */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["email", "მეილი", <BsEnvelope key="e" />],
          ["sms", "SMS", <BsPhone key="s" />],
          ["both", "ორივე", null],
        ].map(([key, label, icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setChannel(key)}
            className={`px-4 h-9 rounded-md text-sm font-bold flex items-center gap-2 ${
              channel === key
                ? "bg-teal_600 text-white"
                : "bg-slate-200 dark:bg-slate-600 dark:text-white text-slate-700"
            }`}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {/* Audience filters */}
      <div className="border border-slate-200 dark:border-slate-600 rounded-md p-3 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-300">
            მიმღებები
          </span>
          <Check k="all" label="ყველა მომხმარებელი" />
        </div>

        <div
          className={`grid grid-cols-3 md:grid-cols-1 gap-3 ${
            filters.all || filters.user_id.trim() ? "opacity-40 pointer-events-none" : ""
          }`}
        >
          <label className="text-xs flex flex-col gap-1">
            <span className="text-slate-500 dark:text-slate-300">ამანათის სტატუსი</span>
            <select
              className={inputCls}
              value={filters.pack_status}
              onChange={(e) => set("pack_status", e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs flex flex-col gap-1">
            <span className="text-slate-500 dark:text-slate-300">საწყობი</span>
            <select
              className={inputCls}
              value={filters.abroad_office_id}
              onChange={(e) => set("abroad_office_id", e.target.value)}
            >
              <option value="">ყველა საწყობი</option>
              {(foreignAdresses || []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.country}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs flex flex-col gap-1">
            <span className="text-slate-500 dark:text-slate-300">რეისი</span>
            <input
              className={inputCls}
              placeholder="AI-385-2"
              value={filters.shipment_code}
              onChange={(e) => set("shipment_code", e.target.value)}
            />
          </label>
        </div>

        <div
          className={`flex flex-wrap gap-4 mt-3 ${
            filters.all || filters.user_id.trim() ? "opacity-40 pointer-events-none" : ""
          }`}
        >
          <Check k="unpaid" label="გადაუხდელი" />
          <Check k="undeclared" label="დეკლარაციის გარეშე" />
          <Check k="no_address" label="მისამართის გარეშე" />
        </div>

        <label className="text-xs flex flex-col gap-1 mt-3">
          <span className="text-slate-500 dark:text-slate-300">
            ან კონკრეტული იუზერ ID-ები (მძიმით) — ფილტრებს გადაფარავს
          </span>
          <input
            className={inputCls}
            placeholder="123, 5711"
            value={filters.user_id}
            onChange={(e) => set("user_id", e.target.value)}
          />
        </label>
      </div>

      {/* Count */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
        <span className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-600 dark:text-white">
          მოიძებნა:{" "}
          <b className="text-teal_600">{counting ? "…" : audience?.total ?? 0}</b>
        </span>
        <span className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-600 dark:text-white">
          მიიღებს:{" "}
          <b className="text-teal_600">{counting ? "…" : reachable}</b>
        </span>
        {audience != null && audience.total > reachable && (
          <span className="text-xs text-amber-600">
            <BsExclamationTriangle className="inline mb-0.5" />{" "}
            {audience.total - reachable} მომხმარებელს არ აქვს{" "}
            {channel === "sms" ? "ვარგისი ნომერი" : "მეილი"}
          </span>
        )}
        {audience?.sample?.length > 0 && (
          <button
            onClick={() => setShowSample((v) => !v)}
            className="text-xs text-teal_600 hover:underline"
          >
            {showSample ? "დამალვა" : "ნიმუშის ნახვა"}
          </button>
        )}
      </div>

      {showSample && audience?.sample?.length > 0 && (
        <div className="mb-4 border border-slate-200 dark:border-slate-600 rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-600 text-slate-500 dark:text-slate-200">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">სახელი</th>
                <th className="text-left p-2">მეილი</th>
                <th className="text-left p-2">ტელეფონი</th>
              </tr>
            </thead>
            <tbody>
              {audience.sample.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-slate-100 dark:border-slate-600 dark:text-slate-200"
                >
                  <td className="p-2 font-mono">{u.id}</td>
                  <td className="p-2">
                    {[u.fname, u.lname].filter(Boolean).join(" ") || u.cname || "—"}
                  </td>
                  <td className="p-2">{u.email || "—"}</td>
                  <td className="p-2">{u.phone || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {channel !== "sms" && (
        <input
          className={`${inputCls} w-full mb-3`}
          placeholder="სათაური (ნაგულისხმევი: MGL Parcel)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      )}

      <textarea
        className="w-full h-28 p-3 text-sm border border-slate-300 dark:border-slate-600
                   rounded-md bg-white dark:bg-slate-800 text-slate-800 dark:text-white mb-3"
        placeholder="ტექსტი"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      {channel !== "email" && (
        <p className="text-xs text-amber-600 mb-3">
          SMS ქართული ტექსტი ავტომატურად გადადის ლათინურზე — გეითვეი უნიკოდს ვერ
          ხატავს.
        </p>
      )}

      <button
        onClick={() => send(false)}
        disabled={sending || counting || !reachable}
        className="rounded-md bg-teal_600 text-white text-sm font-bold px-6 h-10 disabled:opacity-50"
      >
        {sending ? "იგზავნება…" : `გაგზავნა (${reachable})`}
      </button>
    </div>
  );
};

export default BulkSendPanel;
